import { useState } from 'react';
import { trpc } from '../../api/trpc';
import type { AuditFilterState } from './AuditFilters';

interface AuditSavedSearchesProps {
  currentFilters: AuditFilterState;
  onApply: (filters: AuditFilterState) => void;
}

export function AuditSavedSearches({ currentFilters, onApply }: AuditSavedSearchesProps) {
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState('');

  const { data: savedSearches, refetch } = trpc.audit.listSavedSearches.useQuery();
  const createMutation = trpc.audit.createSavedSearch.useMutation({
    onSuccess: () => { void refetch(); setNewName(''); },
  });
  const deleteMutation = trpc.audit.deleteSavedSearch.useMutation({
    onSuccess: () => { void refetch(); },
  });

  const handleSave = () => {
    if (!newName.trim()) return;
    createMutation.mutate({
      name: newName.trim(),
      filters: {
        eventType: currentFilters.eventType || undefined,
        eventCategory: currentFilters.eventCategory || undefined,
        actorId: currentFilters.actorId || undefined,
        startDate: currentFilters.startDate || undefined,
        endDate: currentFilters.endDate || undefined,
        searchQuery: currentFilters.searchQuery || undefined,
        conversationId: currentFilters.conversationId || undefined,
        conversationTitle: currentFilters.conversationTitle || undefined,
      },
    });
  };

  const handleApply = (filters: Record<string, unknown>) => {
    onApply({
      searchQuery: (filters.searchQuery as string) ?? '',
      eventCategory: (filters.eventCategory as string) ?? '',
      eventType: (filters.eventType as string) ?? '',
      actorId: (filters.actorId as string) ?? '',
      startDate: (filters.startDate as string) ?? '',
      endDate: (filters.endDate as string) ?? '',
      timeRange: filters.startDate ? 'custom' : '',
      conversationId: (filters.conversationId as string) ?? '',
      conversationTitle: (filters.conversationTitle as string) ?? '',
    });
    setOpen(false);
  };

  const hasFilters = currentFilters.eventCategory || currentFilters.eventType ||
    currentFilters.actorId || currentFilters.startDate || currentFilters.searchQuery ||
    currentFilters.conversationId;

  return (
    <div className="audit-saved-searches">
      <button className="audit-saved-btn" onClick={() => setOpen(!open)}>
        Saved {'\u25BE'}
      </button>
      {open && (
        <div className="audit-saved-menu">
          <div className="audit-saved-list">
            {savedSearches && savedSearches.length > 0 ? (
              savedSearches.map((s: { id: string; name: string; filters: unknown }) => (
                <div key={s.id} className="audit-saved-item">
                  <button
                    className="audit-saved-item-name"
                    onClick={() => handleApply(s.filters as Record<string, unknown>)}
                  >
                    {s.name}
                  </button>
                  <button
                    className="audit-saved-delete"
                    onClick={() => deleteMutation.mutate({ searchId: s.id })}
                  >
                    {'\u00D7'}
                  </button>
                </div>
              ))
            ) : (
              <p className="audit-saved-empty">No saved searches</p>
            )}
          </div>
          {hasFilters && (
            <div className="audit-saved-create">
              <input
                placeholder="Search name..."
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
              />
              <button onClick={handleSave} disabled={!newName.trim() || createMutation.isPending}>
                Save
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
